import { useEffect, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { Button } from '../../components/Button.jsx';
import { MachinePicker } from './MachinePicker.jsx';

const todayISO = () => new Date().toISOString().slice(0, 10);
const trimOrNull = (v) => { const s = (v ?? '').trim(); return s === '' ? null : s; };
const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

/**
 * Create or edit a maintenance event. Pass `event` to switch to edit mode.
 * Machine is locked in edit mode (the PATCH schema doesn't accept it).
 * Inventory consumption editing lives on the event detail page, not here.
 */
export const NewEventModal = ({ open, onClose, onCreated, onSaved, defaultMachine, event }) => {
  const editing = !!event;
  const { data: contacts } = useApi('/contacts?tag=technician');

  const [title, setTitle] = useState('');
  const [machine, setMachine] = useState({ printerId: null, equipmentId: null });
  const [kind, setKind] = useState('routine');
  const [status, setStatus] = useState('completed');
  const [scheduledAt, setScheduledAt] = useState('');
  const [performedAt, setPerformedAt] = useState(todayISO());
  const [technicianContactId, setTechnicianContactId] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editing) {
      setTitle(event.title ?? '');
      setKind(event.kind ?? 'routine');
      setStatus(event.status ?? 'completed');
      setScheduledAt(toDateInput(event.scheduledAt));
      setPerformedAt(toDateInput(event.performedAt) || todayISO());
      setTechnicianContactId(event.technicianContactId ? String(event.technicianContactId) : '');
      setNotes(event.notes ?? '');
      setMachine({ printerId: event.printerId, equipmentId: event.equipmentId });
    } else {
      setTitle(''); setKind('routine'); setStatus('completed');
      setScheduledAt(''); setPerformedAt(todayISO());
      setTechnicianContactId(''); setNotes('');
      setMachine(defaultMachine ?? { printerId: null, equipmentId: null });
    }
  }, [open, editing, event, defaultMachine]);

  if (!open) return null;

  const save = async () => {
    if (!title.trim()) return;
    if (!editing && !machine.printerId && !machine.equipmentId) {
      setError({ message: 'Pick a machine' }); return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = {
        title: title.trim(),
        kind,
        status,
        scheduledAt: status === 'scheduled' ? (scheduledAt || performedAt || null) : (scheduledAt || null),
        performedAt: status === 'completed' ? (performedAt || todayISO()) : null,
        technicianContactId: technicianContactId ? Number(technicianContactId) : null,
        notes: trimOrNull(notes),
      };
      if (editing) {
        const updated = await api.patch(`/maintenance/events/${event.id}`, body);
        onSaved?.(updated);
      } else {
        const created = await api.post('/maintenance/events', {
          ...body,
          printerId: machine.printerId,
          equipmentId: machine.equipmentId,
          consumptions: [],
        });
        onCreated?.(created);
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: '#E63B7A' }}>
            {editing ? 'Edit maintenance event' : 'Log maintenance event'}
          </p>
        </div>

        <div class="space-y-4 p-5">
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Title</p>
            <input
              autoFocus type="text" value={title}
              onInput={(e) => setTitle(e.currentTarget.value)}
              placeholder="e.g. Roland weekly clean"
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">
              Machine
              {editing && <span class="ml-2 font-medium text-text-muted">· locked — recreate to change</span>}
            </p>
            <MachinePicker value={machine} onChange={setMachine} disabled={editing} />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Kind</p>
              <div class="flex overflow-hidden rounded-md border border-border-strong">
                <button type="button" onClick={() => setKind('routine')}
                  class={`flex-1 px-3 py-1.5 text-[12px] font-semibold ${kind === 'routine' ? 'bg-status-ok text-[#0D2E1E]' : 'text-text-tertiary'}`}>
                  Routine
                </button>
                <button type="button" onClick={() => setKind('visit')}
                  class={`flex-1 px-3 py-1.5 text-[12px] font-semibold ${kind === 'visit' ? 'bg-status-purple text-[#1A0F2E]' : 'text-text-tertiary'}`}>
                  Visit
                </button>
              </div>
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Status</p>
              <select value={status} onChange={(e) => setStatus(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand">
                <option value="completed">Completed</option>
                <option value="scheduled">Scheduled</option>
                <option value="missed">Missed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Scheduled date</p>
              <input type="date" value={scheduledAt} onInput={(e) => setScheduledAt(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Performed date</p>
              <input type="date" value={performedAt} onInput={(e) => setPerformedAt(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
          </div>

          {kind === 'visit' && (
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Technician <span class="font-medium text-text-muted">· optional</span></p>
              <select value={technicianContactId} onChange={(e) => setTechnicianContactId(e.currentTarget.value)}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand">
                <option value="">—</option>
                {(contacts ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ''}</option>)}
              </select>
            </div>
          )}

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Notes</p>
            <textarea rows={3} value={notes} onInput={(e) => setNotes(e.currentTarget.value)}
              class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          {!editing && (
            <p class="text-[11px] text-text-muted">
              Inventory consumption (swabs, solvent, etc.) can be added from the event detail page after saving.
            </p>
          )}

          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed to save')}
            </p>
          )}
        </div>

        <div class="flex justify-end gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy || !title.trim()}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Save event'}
          </Button>
        </div>
      </div>
    </div>
  );
};
